import React, { useEffect, useState } from 'react';
import T from 'prop-types';
import {
  BeadMaterialType,
  GrindingTechnology,
  InnerLinerMaterial,
  MACHINE_TYPE_NAMES,
  MachineTypes,
  NewDialog
} from 'web-components';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import Editor from 'components/Editor/CKEditor';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { editMachineDetails } from 'redux/machines/actions';
import { useDispatch } from 'react-redux';
import { getBackendStringFormat } from 'helpers/stringHandler';
import { getMachineTemplate, MachineModels } from 'attrs/machineModels';

const FILTERED_OUT_MACHINE_TYPES = [MACHINE_TYPE_NAMES.NETZSCH_PUMP_GENERIC, MACHINE_TYPE_NAMES.UNKNOWN];
const machineTypes = MachineTypes.filter(({ value }) => !FILTERED_OUT_MACHINE_TYPES.includes(value));
const getListWithoutDefaultValue = list => list.filter(data => data.value !== 'UNKNOWN');

const zeroToUndefined = value => (value && value > 0 ? +value : undefined);

const EditMachineDetails = ({ open, handleClose, machineDetail }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // -----------------------
  // Touched / Dirty control
  // -----------------------
  const [isModelTouched, setModelTouched] = useState(false);
  const [isManufacturerNameTouched, setManufacturerNameTouched] = useState(false);
  const [isMachineTypeTouched, setMachineTypeTouched] = useState(false);
  const [isMachineOtherTypeTouched, setMachineOtherTypeTouched] = useState(false);
  const [isNameTouched, setNameTouched] = useState(false);
  const [isProductionLineTouched, setProductionLineTouched] = useState(false);
  const [isTagIdTouched, setTagIdTouched] = useState(false);
  const [isTechnicalInformationTouched, setTechnicalInformationTouched] = useState(false);

  const [isSambaTouched, setSambaTouched] = useState(false);
  const [isGrindingTechnologyTouched, setGrindingTechnologyTouched] = useState(false);
  const [isInnerMaterialTouched, setInnerMaterialTouched] = useState(false);
  const [isSieveSizeTouched, setSieveSizeTouched] = useState(false);
  const [isBeadMaterialTouched, setBeadMaterialTouched] = useState(false);
  const [isBeadSize1Touched, setBeadSize1Touched] = useState(false);
  const [isBeadSize2Touched, setBeadSize2Touched] = useState(false);
  const [isBeadFillingLevelTouched, setBeadFillingLevelTouched] = useState(false);

  const resetTouched = () => {
    setModelTouched(false);
    setManufacturerNameTouched(false);
    setMachineTypeTouched(false);
    setMachineOtherTypeTouched(false);
    setNameTouched(false);
    setProductionLineTouched(false);

    setTagIdTouched(false);
    setTechnicalInformationTouched(false);

    setSambaTouched(false);
    setGrindingTechnologyTouched(false);
    setInnerMaterialTouched(false);
    setSieveSizeTouched(false);
    setBeadMaterialTouched(false);
    setBeadSize1Touched(false);
    setBeadSize2Touched(false);
    setBeadFillingLevelTouched(false);
  };

  const isDirty = () =>
    isModelTouched ||
    isManufacturerNameTouched ||
    isMachineTypeTouched ||
    isMachineOtherTypeTouched ||
    isNameTouched ||
    isProductionLineTouched ||
    isTagIdTouched ||
    isTechnicalInformationTouched ||
    isSambaTouched ||
    isGrindingTechnologyTouched ||
    isInnerMaterialTouched ||
    isSieveSizeTouched ||
    isBeadMaterialTouched ||
    isBeadSize1Touched ||
    isBeadSize2Touched ||
    isBeadFillingLevelTouched;

  // -----------------------
  // Form state
  // -----------------------
  const [manufacturerName, setManufacturerName] = useState();
  const onChangeManufacturerName = event => {
    const manufacturerNameParam = event.target.value;
    setManufacturerName(manufacturerNameParam);
    setManufacturerNameTouched(true);
  };

  const [technicalInformation, setTechnicalInformation] = useState();
  const onChangeTechnicalInformation = technicalInformationParam => {
    setTechnicalInformation(getBackendStringFormat(technicalInformationParam));
    setTechnicalInformationTouched(true);
  };

  const [model, setModel] = useState(MachineModels.NETZSCH);
  const handleModelChange = event => {
    const manufacturerParam = event.target.value;
    setModel(manufacturerParam);
    setModelTouched(true);

    if (manufacturerParam !== MachineModels.OTHER && manufacturerParam !== MachineModels.NETZSCH) {
      setManufacturerName(MachineModels.NETZSCH);
      setManufacturerNameTouched(true);
      setTechnicalInformation(getMachineTemplate(manufacturerParam));
      setTechnicalInformationTouched(true);
    } else {
      setManufacturerName();
      setManufacturerNameTouched(false);
      setTechnicalInformation();
      setTechnicalInformationTouched(false);
    }
  };

  const [isSambaChecked, setSambaChecked] = useState(false);
  const onChangeSamba = event => {
    setSambaChecked(event.target.checked);
    setSambaTouched(true);
  };

  const [beadSize2, setBeadSize2] = useState();
  const [machineType, setMachineType] = useState('');
  const handleMachineTypeChange = event => {
    const machineTypeParam = event.target.value;
    setMachineType(machineTypeParam);
    setMachineTypeTouched(true);
    if (machineTypeParam !== 'NETZSCH_MASTER_REFINER') {
      setSambaChecked(false);
      setBeadSize2('');
      // this is a user-driven change due to machine type change
      setSambaTouched(true);
      setBeadSize2Touched(true);
    }
  };

  const [machineOtherType, setMachineOtherType] = useState();
  const handleMachineOtherTypeChange = event => {
    const machineOtherTypeParam = event.target.value;
    setMachineOtherType(machineOtherTypeParam);
    setMachineOtherTypeTouched(true);
  };

  const [comissionIdNumber, setComissionIdNumber] = useState();

  const [name, setName] = useState();
  const onChangeName = event => {
    const nameParam = event.target.value;
    setName(nameParam);
    setNameTouched(true);
  };

  const [productionLine, setProductionLine] = useState();
  const onChangeProductionLine = event => {
    const productionLineParam = event.target.value;
    setProductionLine(productionLineParam);
    setProductionLineTouched(true);
  };

  const [tagId, setTagId] = useState();
  const onChangeTagId = event => {
    const tagIdParam = event.target.value;
    setTagId(getBackendStringFormat(tagIdParam));
    setTagIdTouched(true);
  };

  const [grindingTechnology, setGrindingTechnology] = useState('');
  const onChangeGrindingTechnology = event => {
    const grindingTechnologyParam = event.target.value;
    setGrindingTechnology(getBackendStringFormat(grindingTechnologyParam));
    setGrindingTechnologyTouched(true);
  };

  const [innerMaterial, setInnerMaterial] = useState('');
  const onChangeInnerMaterial = event => {
    const innerMaterialParam = event.target.value;
    setInnerMaterial(getBackendStringFormat(innerMaterialParam));
    setInnerMaterialTouched(true);
  };

  const [sieveSize, setSieveSize] = useState();
  const onChangeSieveSize = event => {
    setSieveSize(zeroToUndefined(event.target.value));
    setSieveSizeTouched(true);
  };

  const [beadMaterial, setBeadMaterial] = useState('');
  const onChangeBeadMaterial = event => {
    const beadMaterialParam = event.target.value;
    setBeadMaterial(getBackendStringFormat(beadMaterialParam));
    setBeadMaterialTouched(true);
  };

  const [beadSize1, setBeadSize1] = useState();
  const onChangeBeadSize1 = event => {
    setBeadSize1(zeroToUndefined(event.target.value));
    setBeadSize1Touched(true);
  };

  const onChangeBeadSize2 = event => {
    setBeadSize2(zeroToUndefined(event.target.value));
    setBeadSize2Touched(true);
  };

  const [beadFillingLevel, setBeadFillingLevel] = useState();
  const onChangeBeadFillingLevel = event => {
    setBeadFillingLevel(zeroToUndefined(event.target.value));
    setBeadFillingLevelTouched(true);
  };

  // -----------------------
  // Get data effect
  // -----------------------
  useEffect(() => {
    // When closing, reset touched so next open starts clear.
    if (!open) {
      resetTouched();
      return;
    }

    // If the user already started editing, do NOT overwrite with refreshed machineDetail.
    if (isDirty()) return;

    if (machineDetail && Object.keys(machineDetail).length > 0) {
      if (machineDetail.brand === MachineModels.NETZSCH) {
        setModel(machineDetail.brand);
        setMachineType(machineDetail.type);
        setComissionIdNumber(machineDetail.commission_number);
        setName(machineDetail.name);
        setProductionLine(machineDetail.production_line);
        setTagId(machineDetail.tag_id);
        setSambaChecked(!!machineDetail.mechanical_config?.samba_system);
        setGrindingTechnology(machineDetail.mechanical_config?.grinding_technology || '');
        setInnerMaterial(machineDetail.mechanical_config?.inner_liner_material || '');
        setSieveSize(zeroToUndefined(machineDetail.mechanical_config?.sieve_size));
        setBeadMaterial(machineDetail.mechanical_config?.bead_material || '');
        setBeadSize1(zeroToUndefined(machineDetail.mechanical_config?.beads_size_1));
        setBeadSize2(zeroToUndefined(machineDetail.mechanical_config?.beads_size_2));
        setBeadFillingLevel(zeroToUndefined(machineDetail.mechanical_config?.beads_filling_level));
      } else {
        setModel(machineDetail.brand);
        setManufacturerName(machineDetail.other_brand);
        setMachineOtherType(machineDetail.other_type);
        setComissionIdNumber(machineDetail.id_number);
        setName(machineDetail.name);
        setProductionLine(machineDetail.production_line);
        setTagId(machineDetail.tag_id);
        setTechnicalInformation(machineDetail.other_brand_technical_info);
      }
    } else {
      setModel(MachineModels.NETZSCH);
      setMachineType();
      setMachineOtherType();
      setComissionIdNumber();
      setName();
      setProductionLine();
      setTagId();
      setSambaChecked(false);
      setGrindingTechnology('');
      setInnerMaterial('');
      setSieveSize();
      setBeadMaterial('');
      setBeadSize1();
      setBeadSize2();
      setBeadFillingLevel();
      setManufacturerName();
      setTechnicalInformation();
    }

    resetTouched();
  }, [machineDetail, open]);

  const handleCancel = () => {
    handleClose();
  };

  function canSave() {
    return (
      !!model &&
      (model !== MachineModels.NETZSCH ? !!manufacturerName : true) &&
      (!!machineType || !!machineOtherType) &&
      !!comissionIdNumber &&
      comissionIdNumber.length > 2 &&
      !!name &&
      !!productionLine
    );
  }

  const handleOk = () => {
    let payload;
    if (model === MachineModels.NETZSCH) {
      payload = {
        machine_details: {
          brand: model,
          commission_number: comissionIdNumber,
          is_notify: true,
          is_preventive: false,
          mechanical_config: {
            bead_material: beadMaterial,
            beads_filling_level: beadFillingLevel,
            beads_size_1: beadSize1,
            beads_size_2: isSambaChecked ? beadSize2 : undefined,
            grinding_technology: grindingTechnology,
            inner_liner_material: innerMaterial,
            samba_system: isSambaChecked,
            sieve_size: sieveSize
          },
          name,
          production_line: productionLine,
          tag_id: tagId,
          type: machineType
        }
      };
    } else {
      payload = {
        machine_details: {
          brand: model,
          id_number: comissionIdNumber,
          is_notify: true,
          is_preventive: false,
          name,
          other_brand: manufacturerName,
          other_brand_technical_info: technicalInformation,
          other_type: machineOtherType,
          production_line: productionLine,
          tag_id: tagId
        }
      };
    }
    payload.machine_details.id = machineDetail?.id;
    dispatch(editMachineDetails(payload));
    handleClose();
  };

  const content = (
    <Box sx={{ width: '978px' }}>
      <Typography sx={{ fontSize: 14, fontWeight: '550', pb: 2 }}>
        {t('machines.edit_modal.basic_information')}
      </Typography>

      <Grid container spacing={2} columns={12}>
        <Grid item xs={4}>
          <FormControl variant="filled" required fullWidth error={!model && isModelTouched}>
            <InputLabel id="model-label">{t('machines.edit_modal.model')}</InputLabel>
            <Select
              fullWidth
              IconComponent={KeyboardArrowDownIcon}
              labelId="model-label"
              id="model-select"
              onChange={handleModelChange}
              value={model}
              disabled
            >
              {Object.values(MachineModels).map(modelType => (
                <MenuItem key={modelType} value={modelType}>
                  {t(`enum.manufacturer_type.${modelType}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {model !== MachineModels.NETZSCH ? (
          <>
            <Grid item xs={4}>
              <TextField
                required
                fullWidth
                id="manufacturer-name"
                label={t('machines.edit_modal.manufacturer_name')}
                variant="filled"
                value={manufacturerName || ''}
                onChange={onChangeManufacturerName}
                error={!manufacturerName && isManufacturerNameTouched}
                disabled={model !== MachineModels.OTHER}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                fullWidth
                id="machine-type-text"
                label={t('machines.edit_modal.machine_type')}
                variant="filled"
                value={machineOtherType}
                onChange={handleMachineOtherTypeChange}
                error={!machineOtherType && isMachineOtherTypeTouched}
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={4}>
            <FormControl variant="filled" required fullWidth error={!machineType && isMachineTypeTouched}>
              <InputLabel id="machine-type-label">{t('machines.edit_modal.machine_type')}</InputLabel>
              <Select
                fullWidth
                IconComponent={KeyboardArrowDownIcon}
                labelId="machine-type-label"
                id="machine-type-select"
                onChange={handleMachineTypeChange}
                value={machineType}
              >
                {machineTypes.map(mt => (
                  <MenuItem key={mt.value} value={mt.value}>
                    {mt.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={4}>
          <TextField
            required
            fullWidth
            id="name"
            label={t('machines.edit_modal.comission_id_number')}
            variant="filled"
            value={comissionIdNumber}
            disabled
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            required
            fullWidth
            id="machine-name"
            label={t('machines.edit_modal.machine_name')}
            variant="filled"
            value={name}
            onChange={onChangeName}
            error={!name && isNameTouched}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            required
            fullWidth
            id="production-line"
            label={t('machines.edit_modal.production_line')}
            variant="filled"
            value={productionLine}
            onChange={onChangeProductionLine}
            error={!productionLine && isProductionLineTouched}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            fullWidth
            id="tag-id"
            label={t('machines.edit_modal.tag_id')}
            variant="filled"
            value={tagId}
            onChange={onChangeTagId}
          />
        </Grid>
      </Grid>

      <Typography sx={{ fontSize: 14, fontWeight: 'bold', pt: 3, pb: 2 }} gutterBottom>
        {t('machines.edit_modal.technical_information')}
      </Typography>

      {model !== MachineModels.NETZSCH ? (
        <Editor value={technicalInformation || ''} onChange={onChangeTechnicalInformation} />
      ) : (
        <Grid container spacing={2} columns={12}>
          <Grid item xs={4}>
            <FormGroup>
              <FormControlLabel
                id="samba_system"
                control={
                  <Checkbox
                    checked={isSambaChecked}
                    onChange={onChangeSamba}
                    disabled={machineType !== 'NETZSCH_MASTER_REFINER'}
                  />
                }
                label={t('machines.edit_modal.samba_system')}
              />
            </FormGroup>
          </Grid>

          <Grid item xs={4}>
            <FormControl variant="filled" fullWidth>
              <InputLabel id="grinding-tech-place-label">{t('machines.edit_modal.grinding_technology')}</InputLabel>
              <Select
                fullWidth
                IconComponent={KeyboardArrowDownIcon}
                labelId="grinding-tech-place-label"
                id="grinding-tech-place-select"
                onChange={onChangeGrindingTechnology}
                value={grindingTechnology}
              >
                <MenuItem value="" sx={{ minHeight: '36px !important' }} />
                {getListWithoutDefaultValue(GrindingTechnology).map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl variant="filled" fullWidth>
              <InputLabel id="inner-material-label">{t('machines.edit_modal.inner_material')}</InputLabel>
              <Select
                fullWidth
                IconComponent={KeyboardArrowDownIcon}
                labelId="inner-material-label"
                id="inner-material-select"
                onChange={onChangeInnerMaterial}
                value={innerMaterial}
              >
                <MenuItem value="" sx={{ minHeight: '36px !important' }} />
                {getListWithoutDefaultValue(InnerLinerMaterial).map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <TextField
              fullWidth
              type="number"
              id="sieve-size"
              label={t('machines.edit_modal.sieve_size')}
              variant="filled"
              value={sieveSize}
              onChange={onChangeSieveSize}
              InputProps={{
                inputProps: {
                  min: '0',
                  max: '100000',
                  step: '1'
                }
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <FormControl variant="filled" fullWidth>
              <InputLabel id="bead-material-label">{t('machines.edit_modal.bead_material')}</InputLabel>
              <Select
                fullWidth
                IconComponent={KeyboardArrowDownIcon}
                labelId="bead-material-label"
                id="bead-material-select"
                onChange={onChangeBeadMaterial}
                value={beadMaterial}
              >
                <MenuItem value="" sx={{ minHeight: '36px !important' }} />
                {getListWithoutDefaultValue(BeadMaterialType).map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <TextField
              fullWidth
              type="number"
              id="bead-size-1"
              label={t('machines.edit_modal.bead_size_1')}
              variant="filled"
              value={beadSize1}
              onChange={onChangeBeadSize1}
              InputProps={{
                inputProps: {
                  min: '0',
                  max: '100000',
                  step: '1'
                }
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              fullWidth
              type="number"
              id="bead-size-2"
              label={t('machines.edit_modal.bead_size_2')}
              variant="filled"
              value={beadSize2}
              onChange={onChangeBeadSize2}
              InputProps={{
                inputProps: {
                  min: '0',
                  max: '100000',
                  step: '1'
                }
              }}
              disabled={!isSambaChecked}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              fullWidth
              type="number"
              id="bead-filling-level"
              label={t('machines.edit_modal.bead_filling_level')}
              variant="filled"
              value={beadFillingLevel}
              onChange={onChangeBeadFillingLevel}
              InputProps={{
                inputProps: {
                  min: '0',
                  max: '100',
                  step: '1'
                }
              }}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );

  return (
    <NewDialog
      open={open}
      canSave={canSave()}
      handleClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      handleCancel={handleCancel}
      handleOk={handleOk}
      title={t('machines.edit_modal.edit_title')}
      subTitle={t('machines.edit_modal.edit_subtitle')}
      content={content}
      cancelCaption={t('default_actions.cancel')}
      okCaption={t('default_actions.save')}
      displayActionBar="flex"
      justifyContentActionBar="space-between"
      maxWidth="98%"
      maxHeight="98%"
    />
  );
};

EditMachineDetails.propTypes = {
  open: T.bool.isRequired,
  handleClose: T.func.isRequired,
  machineDetail: T.instanceOf(Object)
};

EditMachineDetails.defaultProps = {
  machineDetail: null
};

export default EditMachineDetails;
